import { useCallback, useEffect, useReducer, useRef } from 'react';

import { SortOrder, Status } from '@ariksa/notification';
import { Severity } from '@ariksa/notification/api';
import dayjs from 'dayjs';
import { filter, find, includes, isEmpty, map, split, toUpper } from 'lodash';
import isArray from 'lodash/isArray';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareEffect, useLocation } from 'react-use';

import { alertsCloudProviderOptionsWithAll } from 'components/DataDisplay';
import { capsSeverityOptions } from 'components/DataDisplay/Utils/renderSeverity';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { selectAlerts } from 'containers/Findings/Alerts/selectors';
import { actions } from 'containers/Findings/Alerts/slice';
import { FindingsIssuesSearchParamProps } from 'containers/Findings/Alerts/types';
import {
  alertStatusOptions,
  exceptionAlertStatusOption,
} from 'containers/Findings/Alerts/utils';
import { getBlueprintIcon } from 'containers/PolicyHub/utils';
import { useSharedApis } from 'containers/SharedState/hooks/useSharedApis';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';
import { useSearchParams } from 'hooks/useSearchParams';
import { toTitleCase } from 'utils/string';

export const useAlerts = () => {
  const dispatch = useDispatch();
  const {
    aggregatedFindings,
    filters,
    alerts,
    startDate,
    sortByField,
  } = useSelector(selectAlerts);
  const {
    aiParamExtraction,
    blueprints,
    alertResourceTypes,
    alertCategories,
  } = useSelector(selectSharedState);
  const { withAccessBoundary, accountId, environmentId } = useAccessBoundary();
  const params = useSearchParams<FindingsIssuesSearchParamProps>();
  const { updateValueInURL } = useSearchParamFunctions();
  const { getResourceAlias, getAlertResourceTypeOptions } = useResourceType();
  const { getBlueprints } = useSharedApis();
  const navigate = useNavigate();
  const location = useLocation();
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      blueprintId: {},
      resourceType: [],
      alertCategory: [],
      severity: [],
      alertCategoryOptions: [],
      resourceTypeOptions: [],
      regionOptions: [],
      blueprintOptions: [],
      region: {},
      alertStatus: alertStatusOptions[0],
      cloud: alertsCloudProviderOptionsWithAll[0],
    },
  );

  useDeepCompareEffect(() => {
    const data = aiParamExtraction.data;
    const alertCategory = !!data?.alert_category
      ? [data?.alert_category]
      : !!data?.alertCategories
      ? data?.alertCategories
      : undefined;
    const severity = !!data?.severity
      ? [data?.severity]
      : !!data?.severities
      ? split(data?.severities, ',')
      : undefined;
    const resourceType = !!data?.resource_type
      ? [data?.resource_type]
      : !!data?.resourceTypes
      ? split(data?.resourceTypes, ',')
      : undefined;
    const categoryClass = data?.category_class || undefined;
    const search = data?.search || undefined;
    let searchParams = '';
    const setValue = (f, v) => {
      searchParams = searchParams + (!!searchParams ? '&' : '') + f + '=' + v;
    };

    if (!!alertCategory) setValue('alertCategory', alertCategory);
    if (!!severity) setValue('severity', severity);
    if (!!resourceType) setValue('resourceType', resourceType);
    if (!!categoryClass) setValue('categoryClass', categoryClass);
    if (!!search) setValue('search', search);
    if (!!searchParams) navigate(location.pathname + '?' + searchParams);
    dispatch(
      actions.updateAllFilters({
        searchString: data?.search || undefined,
        startTime: data?.start_date || undefined,
        endTime: data?.end_date || undefined,
        category_class: data?.category_class || undefined,
      }),
    );
  }, [aiParamExtraction.data, dispatch]);

  useEffect(() => {
    getBlueprints();
  }, [getBlueprints]);

  useEffect(() => {
    const alertStatus = params?.alertStatus;
    updateState({
      alertStatus: !!alertStatus
        ? // Include the link-only Exception status so its filter is sent.
          find(
            [...alertStatusOptions, exceptionAlertStatusOption],
            o => o.value === alertStatus,
          )
        : alertStatusOptions[0],
    });
  }, [params.alertStatus]);

  //set alert category options
  useEffect(() => {
    const options = [
      ...map(alertCategories.data.categories, (count, o) => ({
        label: toTitleCase(o),
        value: o,
      })),
    ];
    updateState({
      alertCategoryOptions: options,
    });
  }, [alertCategories.data]);

  //set alert category
  useEffect(() => {
    const categories = params.alertCategory;
    const alertCategories = !!categories
      ? map(split(categories, ','), o => ({
          label: toTitleCase(o),
          value: o,
        }))
      : [];
    updateState({
      alertCategory: alertCategories,
    });
  }, [dispatch, params.alertCategory, state.alertCategoryOptions]);

  //set severity
  useEffect(() => {
    const severities = params?.severity;
    updateState({
      severity: !!severities
        ? filter(capsSeverityOptions, o =>
            includes(split(severities, ','), o.value),
          )
        : [],
    });
  }, [dispatch, params?.severity, state.severityOptions]);

  //set cloud
  useEffect(() => {
    const cloud = params?.cloud;
    updateState({
      cloud: !!cloud
        ? find(alertsCloudProviderOptionsWithAll, o => o.value === cloud)
        : alertsCloudProviderOptionsWithAll[0],
    });
  }, [dispatch, params?.cloud]);

  //set resource type options
  useEffect(() => {
    const options = getAlertResourceTypeOptions(alertResourceTypes.data);
    updateState({
      resourceTypeOptions: options,
    });
  }, [alertResourceTypes.data, getAlertResourceTypeOptions]);

  //set resource type
  useEffect(() => {
    const types = params?.resourceType;
    updateState({
      resourceType: !!types
        ? filter(state.resourceTypeOptions, o =>
            includes(split(types, ','), o.value),
          )
        : [],
    });
  }, [
    dispatch,
    params.resourceType,
    getResourceAlias,
    state.resourceTypeOptions,
  ]);

  //set blueprint options
  useEffect(() => {
    const options = [
      { label: 'Blueprint', value: undefined },
      ...(blueprints.data?.map(item => {
        return {
          label: item.name,
          value: item?.id,
          icon: getBlueprintIcon(
            item.created_by !== 'system',
            item.compliance ?? item.name,
          ),
        };
      }) ?? []),
    ];

    updateState({
      blueprintOptions: options,
    });
  }, [blueprints.data]);

  //set blueprint_id
  useEffect(() => {
    const blueprintId = params?.blueprintId;
    updateState({
      blueprintId: !!blueprintId
        ? find(state.blueprintOptions, o => o.value === blueprintId)
        : state.blueprintOptions[0],
    });
  }, [dispatch, params.blueprintId, state.blueprintOptions]);

  const updateValue = useCallback(
    (field, value) => {
      updateValueInURL(
        field,
        isArray(value) ? map(value, o => o?.value)?.join(',') : value?.value,
      );
    },
    [updateValueInURL],
  );

  const getDetails = useCallback(
    results => {
      if (isEmpty(results)) return;
      const uuids = map(results, o => o.entity_uuid);

      //get risk context
      if (state?.alertStatus?.value !== 'Resolved')
        dispatch(
          sharedStateActions.getRiskContext({
            q: {
              riskContextRequest: {
                uuids,
              },
            },
          }),
        );

      dispatch(
        sharedStateActions.getAlertListStatus({
          q: { alertId: map(results, o => o.uuid) },
        }),
      );
    },
    [dispatch, state.alertStatus],
  );

  const getAggregatedFindings = useCallback(() => {
    const date = !!startDate?.value
      ? {
          startDate: dayjs()
            .subtract(startDate.value, 'days')
            .utc()
            .format('YYYY-MM-DD'),
          endDate: dayjs().utc().format('YYYY-MM-DD'),
        }
      : {};
    dispatch(
      actions.getAggregatedFindings({
        q: withAccessBoundary({
          search:
            params?.search ??
            (!!filters.searchString ? filters.searchString : undefined),
          pageNumber: aggregatedFindings.page.info.page_number,
          pageSize: aggregatedFindings.page.info.page_size,
          alertCategories: params?.alertCategory
            ? split(params?.alertCategory, ',')
            : undefined,
          resourceTypes: params?.resourceType
            ? split(params?.resourceType, ',')
            : undefined,
          severities: params?.severity
            ? (split(params?.severity, ',') as Severity[])
            : undefined,
          sortField: sortByField.sortField,
          sortOrder: toUpper(sortByField.sortOrder) as SortOrder,
          ...date,
        }),
        onSuccess: res => {
          !isEmpty(res?.aggregated_alerts) &&
            getDetails(res?.aggregated_alerts);
        },
      }),
    );
  }, [
    dispatch,
    withAccessBoundary,
    aggregatedFindings.page.info,
    filters.searchString,
    startDate?.value,
    getDetails,
    sortByField,
    params?.alertCategory,
    params?.severity,
    params?.resourceType,
    params?.search,
  ]);

  // Filter effects re-create equal filter arrays while options load; remember
  // the last request so the page effect does not refetch identical queries.
  const lastAlertsRequest = useRef('');
  const getAlerts = useCallback(
    (options?: { skipIfUnchanged?: boolean }) => {
      if (!!accountId || !!environmentId) {
        let date = !!startDate?.value
          ? {
              startDate: dayjs()
                .subtract(startDate.value, 'days')
                .utc()
                .format('YYYY-MM-DD'),
              endDate: dayjs().utc().format('YYYY-MM-DD'),
            }
          : {};
        if (!!filters.startTime) {
          date = {
            startDate: dayjs(filters.startTime).utc().format('YYYY-MM-DD'),
            endDate: dayjs(filters.endTime).utc().format('YYYY-MM-DD'),
          };
        }
        const resolved =
          state?.alertStatus?.value === 'Resolved'
            ? { status: Status.Resolved }
            : state?.alertStatus?.value === 'Exception'
            ? { status: Status.Resolved, resolvedBy: 'exception' }
            : {};
        const entityUuid = !!params.entity_uuid
          ? { entityUuid: params.entity_uuid }
          : {};
        const blueprint = !!params.blueprintId
          ? { blueprintId: [params.blueprintId] }
          : {};

        const q = {
          accountId,
          environmentId,
          ...resolved,
          cloud: params?.cloud || undefined,
          searchString: params?.search || filters.searchString || undefined,
          alertCategories: !isEmpty(state?.alertCategory)
            ? map(state?.alertCategory, o => o.value)
            : undefined,
          resourceTypes: state?.resourceType
            ? map(state?.resourceType, o => o.value)
            : undefined,
          severities: !isEmpty(state?.severity)
            ? (map(state?.severity, o => o.value) as Severity[])
            : undefined,
          ...entityUuid,
          ...date,
          region: params?.region,
          ...blueprint,
          pageNumber: alerts.page.info.page_number,
          pageSize: alerts.page.info.page_size,
          sortField: sortByField.sortField,
          sortOrder: sortByField.sortOrder,
        };
        const requestKey = JSON.stringify(q);
        if (
          options?.skipIfUnchanged &&
          requestKey === lastAlertsRequest.current
        )
          return;
        lastAlertsRequest.current = requestKey;
        dispatch(
          actions.getAlerts({
            q,
            onSuccess: res => {
              getDetails(res?.results);
            },
          }),
        );
      }
    },
    [
      accountId,
      environmentId,
      startDate.value,
      filters.startTime,
      filters.searchString,
      filters.endTime,
      state?.alertStatus,
      state?.alertCategory,
      state?.resourceType,
      state?.severity,
      params.entity_uuid,
      params.blueprintId,
      params?.cloud,
      params?.search,
      params?.region,
      dispatch,
      alerts.page.info.page_number,
      alerts.page.info.page_size,
      sortByField.sortField,
      sortByField.sortOrder,
      getDetails,
    ],
  );

  return {
    getAggregatedFindings,
    getAlerts,
    updateValue,
    severity: state.severity,
    alertStatus: state.alertStatus,
    alertCategoryOptions: state.alertCategoryOptions,
    alertCategory: state.alertCategory,
    cloud: state.cloud,
    resourceTypeOptions: state.resourceTypeOptions,
    resourceType: state.resourceType,
    blueprintOptions: state.blueprintOptions,
    blueprintId: state.blueprintId,
  };
};
